/*
 * Omni Performance — ONE governed derivation for the estate panel.
 *
 * The panel used to run two engines side by side: the headline KPI strip was
 * an aggregate of per-channel topline fetches over the *programmatic* channel
 * list (priced through the programmatic campaign rate card), while the
 * Platform Contribution bars were projected from canonical estate reporting.
 * Two engines over two platform scopes cannot reconcile, and they did not:
 * a headline that excluded YouTube sat directly above a bar chart that
 * included it, and the "N contributing platforms" sentence counted the
 * channels the KPI engine had admitted rather than the platforms drawn below.
 *
 * This module replaces both with one projection of the canonical platform
 * rows the surrounding campaign context already resolved. Every number the
 * panel states — tile totals, bar values, shares, platform counts and the
 * leading surface — is derived HERE, from the same rows, so the tiles are the
 * column sums of the bars by construction rather than by coincidence.
 *
 * Governance held here:
 *  - No arithmetic invents a platform. Rows arrive from canonical reporting;
 *    this module only folds, sorts, shares and formats them.
 *  - Participation is per metric. A platform that reported no clicks is not
 *    a "clicks platform": it leaves the clicks basis entirely rather than
 *    entering it as a zero, so the clicks tile and the clicks bars state the
 *    same platform count. Same rule the canonical rate basis already applies.
 *  - A share is always taken against the total the panel is displaying for
 *    that metric — never against a different pool.
 *
 * Byte-identical in Cortex and Quantum (pinned by crossRepositoryParity):
 * a second copy is exactly how the two portals would start reporting
 * different estate totals for the same advertiser and window.
 */

export const OMNI_PERFORMANCE_METRICS = Object.freeze([
  Object.freeze({ key: "spend", label: "Spend", kind: "currency" }),
  Object.freeze({ key: "impressions", label: "Impressions", kind: "count" }),
  Object.freeze({ key: "clicks", label: "Clicks", kind: "count" }),
]);

export const OMNI_PERFORMANCE_METRIC_KEYS = Object.freeze(
  OMNI_PERFORMANCE_METRICS.map((metric) => metric.key)
);

/*
 * A reported figure only counts when it is a real, positive observation.
 * Absent stays absent (never 0), and a zero never mints participation.
 */
const positiveOrZero = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
};

export function resolveOmniPerformanceMetric(metricKey) {
  return (
    OMNI_PERFORMANCE_METRICS.find((metric) => metric.key === metricKey) ||
    OMNI_PERFORMANCE_METRICS[0]
  );
}

/**
 * Canonical platform rows → the one model the panel renders.
 *
 * @param {Array<{key: string, label: string, spend: *, impressions: *, clicks: *}>} platformRows
 *   Rows as canonical reporting stated them for the approved window.
 * @returns {{
 *   rows: Array,
 *   byMetric: Object,
 *   contributingPlatformCount: number,
 *   leadPlatform: ?Object,
 *   hasEvidence: boolean
 * }}
 */
export function buildOmniPerformanceModel(platformRows) {
  const rows = (Array.isArray(platformRows) ? platformRows : [])
    .map((row) => {
      const key = String(row?.key || "").trim().toLowerCase();
      if (!key) return null;
      return {
        key,
        label: String(row?.label || key),
        spend: positiveOrZero(row?.spend),
        impressions: positiveOrZero(row?.impressions),
        clicks: positiveOrZero(row?.clicks),
      };
    })
    .filter(Boolean)
    /*
     * Admission is evidence, never connectivity: a platform enters the estate
     * when canonical reporting carries ANY delivered volume for it in this
     * window. This is the same two-tier rule the welcome preview applies.
     */
    .filter((row) => OMNI_PERFORMANCE_METRIC_KEYS.some((key) => row[key] > 0))
    .sort((a, b) => b.spend - a.spend || b.impressions - a.impressions);

  const byMetric = {};
  OMNI_PERFORMANCE_METRICS.forEach((metric) => {
    const contributing = rows
      .map((row) => ({ key: row.key, label: row.label, value: row[metric.key] }))
      .filter((row) => row.value > 0)
      .sort((a, b) => b.value - a.value);
    const total = contributing.reduce((sum, row) => sum + row.value, 0);
    const max = contributing.length ? contributing[0].value : 0;

    byMetric[metric.key] = {
      key: metric.key,
      label: metric.label,
      kind: metric.kind,
      total,
      platformCount: contributing.length,
      rows: contributing.map((row) => ({
        ...row,
        // Against the displayed total for THIS metric — the denominator the
        // tile above the bars is stating, and nothing else.
        share: total > 0 ? (row.value / total) * 100 : 0,
        // A hairline for a real but tiny contribution: a drawn zero would
        // read as "no delivery" for a platform that did deliver.
        widthPercent: max > 0 ? Math.max((row.value / max) * 100, 2) : 0,
      })),
    };
  });

  return {
    rows,
    byMetric,
    contributingPlatformCount: rows.length,
    leadPlatform: rows.length ? { key: rows[0].key, label: rows[0].label } : null,
    hasEvidence: rows.length > 0,
  };
}

/*
 * Headline figures are stated in full. A compacted "$7.5K" headline above
 * bars that also read "$5.8K" is precisely what made the panel impossible to
 * reconcile by eye — the reader could not tell whether the parts summed.
 */
export function formatOmniPerformanceValue(value, kind = "count") {
  // `Number(null)` is 0, not NaN — an absent figure must never format as a
  // reported zero, so absence is checked before the numeric coercion.
  if (value == null || value === "") return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  if (kind === "currency") {
    const digits = numeric !== 0 && Math.abs(numeric) < 1 ? 2 : 0;
    return `$${numeric.toLocaleString("en-US", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })}`;
  }
  return numeric.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

export function describeOmniPlatformCount(platformCount) {
  const count = Number(platformCount) || 0;
  return `${count} reporting platform${count === 1 ? "" : "s"}`;
}

/*
 * What each tile is actually counting, in the panel's own voice. The platform
 * count is per metric, so the sentence under a tile always names the exact
 * set of bars that sums to it.
 */
export function describeOmniMetricBasis(metricKey, platformCount) {
  const platforms = describeOmniPlatformCount(platformCount);
  if (metricKey === "spend") return `Client-billed spend across ${platforms}`;
  if (metricKey === "impressions") return `Impressions reported by ${platforms}`;
  if (metricKey === "clicks") return `Clicks reported by ${platforms}`;
  return `Canonical total across ${platforms}`;
}

/*
 * The one-line reconciliation statement under the contribution header: the
 * bars below it sum to this figure, and this figure is the tile above.
 */
export function describeOmniContributionTotal(metricBasis) {
  if (!metricBasis || !metricBasis.platformCount) return "";
  return `${metricBasis.label} · ${formatOmniPerformanceValue(
    metricBasis.total,
    metricBasis.kind
  )} across ${describeOmniPlatformCount(metricBasis.platformCount)}`;
}

/**
 * The panel's KPI tiles — one per canonical metric, stated only when the
 * estate actually reported it.
 */
export function buildOmniPerformanceMetricCards(model) {
  if (!model || !model.hasEvidence) return [];
  return OMNI_PERFORMANCE_METRICS.map((metric) => {
    const basis = model.byMetric?.[metric.key];
    if (!basis || !basis.platformCount) return null;
    return {
      id: metric.key,
      label: metric.label,
      value: formatOmniPerformanceValue(basis.total, metric.kind),
      meta: describeOmniMetricBasis(metric.key, basis.platformCount),
      type: "metric",
    };
  }).filter(Boolean);
}
